import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, ArrowRight, Loader2, CheckCircle, XCircle } from 'lucide-react';
import './ArtistAuth.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistRegister = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/invite/${token}/validate`);
      const data = await res.json();

      if (res.ok) {
        setInviteData(data);
      } else {
        setError(data.detail || 'Invalid or expired invitation link');
      }
    } catch (err) {
      setError('Failed to validate invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/artist/invite/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Account created successfully! You can now log in.');
        navigate('/artist/login');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Failed to complete registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="artist-auth-page">
        <div className="auth-container">
          <div className="auth-card" style={{ padding: '60px 32px', textAlign: 'center' }}>
            <Loader2 size={40} className="spinner" style={{ color: 'var(--cyan)', marginBottom: '16px' }} />
            <p style={{ color: 'var(--silver)' }}>Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="artist-auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <span className="logo-text">AMR</span>
              <span className="logo-subtext">Artist Portal</span>
            </Link>
          </div>

          <div className="auth-card" style={{ padding: '48px 32px', textAlign: 'center' }}>
            <XCircle size={48} style={{ color: '#ff4444', marginBottom: '16px' }} />
            <h2 style={{ color: 'var(--white)', marginBottom: '12px' }}>Invalid Invitation</h2>
            <p style={{ color: 'var(--silver)', marginBottom: '24px' }}>{error}</p>
            <Link to="/artist/login" className="auth-submit-btn" style={{ display: 'inline-flex', textDecoration: 'none' }}>
              Go to Login
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="artist-auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-text">AMR</span>
            <span className="logo-subtext">Artist Portal</span>
          </Link>
        </div>

        <div className="auth-card">
          <div className="auth-card-header">
            <CheckCircle size={32} style={{ color: 'var(--cyan)', marginBottom: '12px' }} />
            <h2>Welcome, {inviteData?.artist_name}!</h2>
            <p>Complete your registration to access the Artist Portal</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="invite-info" style={{ 
              background: 'rgba(0, 255, 255, 0.05)', 
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: 'var(--silver)', fontSize: '0.9rem', margin: 0 }}>
                <strong style={{ color: 'var(--white)' }}>Email:</strong> {inviteData?.email}
              </p>
            </div>

            <div className="form-group">
              <label>
                <Lock size={16} />
                Create Password *
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Min 8 characters"
                data-testid="register-password"
              />
            </div>

            <div className="form-group">
              <label>
                <Lock size={16} />
                Confirm Password *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={submitting}
              data-testid="register-submit"
            >
              {submitting ? (
                <Loader2 size={20} className="spinner" />
              ) : (
                <>
                  Complete Registration
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="auth-footer">
          <Link to="/">← Back to main site</Link>
        </div>
      </div>
    </div>
  );
};

export default ArtistRegister;
